import { useEffect, useRef } from 'react';

interface SegmentedProgressProps {
  total: number;
  visited: Set<number>;
  otherVotes?: Set<number>;
  currentIndex: number;
  batchSize: number;
  onJump: (index: number) => void;
  unblockedSeconds?: number;
  isAudit?: boolean;
  annotatorVotes?: { name: string; votes: Set<number> }[] | undefined;
  labels?: Record<string, boolean | null>;
}

export function SegmentedProgress({
  total,
  visited,
  otherVotes,
  currentIndex,
  batchSize,
  onJump,
  unblockedSeconds = 0,
  isAudit,
  annotatorVotes,
  labels,
}: SegmentedProgressProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const unblockedPercent =
    total > 0 ? Math.max(0, Math.min(100, (unblockedSeconds / total) * 100)) : 0;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.clearRect(0, 0, rect.width, rect.height);

    if (total <= 0) return;

    const segmentWidth = rect.width / total;
    const gap = Math.max(0, segmentWidth > 3 ? 1 : 0);
    const drawWidth = segmentWidth - gap;

    // Colors
    const colorPending = '#27272a'; // brand-dark
    const colorVisited = '#f97316'; // brand-orange
    const colorCurrent = '#ffffff'; // white
    const colorOtherVote = '#10b981'; // emerald-500
    const colorFinalTrue = '#10b981'; // emerald-500
    const colorFinalFalse = '#ef4444'; // red-500
    const colorFinalNull = '#eab308'; // yellow-500 (conflict/unset)
    const colorAnnotator = '#3b82f6'; // blue-500

    // Draw background
    ctx.fillStyle = colorPending;
    ctx.fillRect(0, 0, rect.width, rect.height);

    if (isAudit && annotatorVotes) {
      // Audit Mode: Stacked Rows
      // Rows: Reviewed, Final, Annotator 1, Annotator 2...
      const rows = 2 + annotatorVotes.length;
      const rowHeight = rect.height / rows;

      // 1. Reviewed (Visited)
      ctx.fillStyle = colorVisited;
      visited.forEach((idx) => {
        if (idx >= 0 && idx < total) {
          ctx.fillRect(idx * segmentWidth, 0, drawWidth, rowHeight - 1);
        }
      });

      // 2. Final Result (Labels) - Only if visited
      visited.forEach((idx) => {
        if (idx >= 0 && idx < total) {
          const val = labels?.[String(idx)];
          ctx.fillStyle =
            val === true ? colorFinalTrue : val === false ? colorFinalFalse : colorFinalNull;
          ctx.fillRect(idx * segmentWidth, rowHeight, drawWidth, rowHeight - 1);
        }
      });

      // 3. Annotators
      annotatorVotes.forEach((av, i) => {
        const y = (2 + i) * rowHeight;
        ctx.fillStyle = colorAnnotator;
        av.votes.forEach((idx) => {
          if (idx >= 0 && idx < total) {
            ctx.fillRect(idx * segmentWidth, y, drawWidth, rowHeight - 1);
          }
        });
      });
    } else {
      // Normal Mode: 2 Rows (Reviewed, Result)
      const rows = 2;
      const rowHeight = rect.height / rows;

      // 1. Reviewed (Visited)
      ctx.fillStyle = colorVisited;
      visited.forEach((idx) => {
        if (idx >= 0 && idx < total) {
          ctx.fillRect(idx * segmentWidth, 0, drawWidth, rowHeight - 1);
        }
      });

      // 2. Result (Labels)
      for (let idx = 0; idx < total; idx++) {
        const val = labels?.[String(idx)];
        // Default to true (Qualified) if undefined/null in normal mode context?
        // Or false? Store initializes to true.
        ctx.fillStyle =
          val === true ? colorFinalTrue : val === false ? colorFinalFalse : colorFinalNull;
        ctx.fillRect(idx * segmentWidth, rowHeight, drawWidth, rowHeight - 1);
      }

      // Draw other votes (small indicators at bottom of Result row)
      if (otherVotes && otherVotes.size > 0) {
        ctx.fillStyle = colorOtherVote;
        otherVotes.forEach((idx) => {
          if (idx >= 0 && idx < total) {
            const h = rowHeight * 0.3;
            ctx.fillRect(idx * segmentWidth, rect.height - h, drawWidth, h);
          }
        });
      }
    }

    // Draw current window indicator (overlay)

    ctx.fillStyle = colorCurrent;
    const windowEnd = Math.min(total, currentIndex + batchSize);
    for (let i = currentIndex; i < windowEnd; i++) {
      ctx.globalAlpha = 0.6;
      ctx.fillRect(i * segmentWidth, 0, drawWidth, rect.height);
      ctx.globalAlpha = 1.0;
    }
  }, [total, visited, otherVotes, currentIndex, batchSize, isAudit, annotatorVotes, labels]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const index = Math.floor((x / rect.width) * total);
    onJump(Math.max(0, Math.min(total - 1, index)));
  };

  return (
    <div className="w-full h-full group cursor-pointer flex flex-col">
      <div className="w-full flex-1 min-h-0 rounded bg-brand-dark overflow-hidden relative border border-brand-border/50 group-hover:border-brand-orange/50 transition-colors">
        <canvas ref={canvasRef} className="w-full h-full block" onClick={handleClick} />
      </div>
      <div
        className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-brand-dark/80"
        aria-label="Unblocked video range"
        aria-valuemax={total}
        aria-valuemin={0}
        aria-valuenow={Math.max(0, Math.min(total, unblockedSeconds))}
        role="progressbar"
      >
        <div
          className="h-full rounded-full bg-blue-500"
          style={{ width: `${unblockedPercent}%` }}
        />
      </div>
    </div>
  );
}
